/**
 * Error group row. Screen spec S16 zone 2.
 *
 * Errors arrive in thousands and they are the same twelve mistakes over and
 * over, so the unit on this screen is the group, never the occurrence. One
 * row per distinct thing that is going wrong, with how many times it happened
 * and when it started, and the whole error block (4.3) underneath when the row
 * is opened.
 *
 * DEVIATION FROM SPEC S16, recorded. The spec leads the row with the machine
 * message in mono. This one leads with the plain sentence and carries the
 * machine message underneath it, truncated to one line, in mono. Two reasons.
 * A list whose first column is "TypeError: Cannot read properties of null" is
 * a list Nina cannot triage, and triage is the entire job of this page: which
 * of these three do I get fixed first. And nothing is lost for Priya, because
 * the verbatim message is still on the row, still in mono, one line down, and
 * still the string the fix request carries. The row answers "what is wrong"
 * before it answers "what did the runtime call it".
 *
 * The count is a number, never a bar and never a colour ramp. Fourteen errors
 * on a small app and fourteen errors on a large one are different facts, and
 * this product does not know enough to draw the difference honestly.
 *
 * A resolved group is a different row on purpose: no chevron, no fix request,
 * a check glyph and the word. There is nothing to act on, and a row that still
 * offered an action would keep a solved thing on the list of open worries.
 */

import type { ReactNode } from 'react';
import { useId } from 'react';
import { whenLabel } from './DeployRow';
import { shortSha } from '../../lib/format';
import { useAdvanced } from '../../lib/hooks/use-advanced';
import { t } from '../../lib/i18n/t';
import type { ErrorGroup } from '../../lib/types';
import '../../styles/components/error-group.css';

/** "14" with a name a screen reader can read, and a real singular. */
function countLabel(n: number): string {
  return n === 1 ? t('Seen once') : t('Seen {n} times', { n });
}

export interface ErrorGroupRowProps {
  group: ErrorGroup;
  open: boolean;
  onToggle: () => void;
  /** The error block for this group. Mounted only while the row is open. */
  children: ReactNode;
}

export function ErrorGroupRow({ group, open, onToggle, children }: ErrorGroupRowProps) {
  const panelId = useId();
  const [advanced] = useAdvanced();

  return (
    <li className="egrp" data-open={open ? 'yes' : 'no'}>
      <button
        type="button"
        className="egrp__head"
        aria-expanded={open}
        aria-controls={panelId}
        onClick={onToggle}
      >
        <span className="egrp__chev" data-open={open ? 'yes' : 'no'} aria-hidden="true">
          <svg viewBox="0 0 16 16" width="10" height="10" focusable="false">
            <path
              d="M6 3.5 10.5 8 6 12.5"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.6"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </span>

        <span className="egrp__text">
          <span className="egrp__say">{t(group.summary)}</span>
          <span className="egrp__raw mono" title={group.raw}>
            {group.raw}
          </span>
        </span>

        <span className="egrp__count mono" aria-label={countLabel(group.count)}>
          {group.count}
        </span>

        <span className="egrp__times">
          <span className="egrp__time">
            {t('First seen {when}', { when: whenLabel(group.firstSeen) })}
          </span>
          <span className="egrp__time">
            {t('Last seen {when}', { when: whenLabel(group.lastSeen) })}
          </span>
          {advanced && group.deployHash ? (
            <span className="egrp__time mono" title={group.deployHash}>
              {shortSha(group.deployHash)}
            </span>
          ) : null}
        </span>
      </button>

      {open ? (
        <div className="egrp__panel" id={panelId} role="region" aria-label={t(group.summary)}>
          {children}
        </div>
      ) : null}
    </li>
  );
}

export interface ResolvedGroupRowProps {
  group: ErrorGroup;
}

/** A group that stopped happening. A fact, not a worry, and not actionable. */
export function ResolvedGroupRow({ group }: ResolvedGroupRowProps) {
  const [advanced] = useAdvanced();

  return (
    <li className="egrp" data-tone="resolved">
      <div className="egrp__head" data-static="yes">
        <span className="egrp__check" aria-hidden="true">
          <svg viewBox="0 0 16 16" width="12" height="12" focusable="false">
            <path
              d="M3 8.5 6.5 12 13 4.5"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </span>

        <span className="egrp__text">
          <span className="egrp__say">{t(group.summary)}</span>
          <span className="egrp__raw mono" title={group.raw}>
            {group.raw}
          </span>
        </span>

        <span className="egrp__count mono" aria-label={countLabel(group.count)}>
          {group.count}
        </span>

        <span className="egrp__times">
          <span className="egrp__time">{t('Stopped')}</span>
          <span className="egrp__time">
            {t('Last seen {when}', { when: whenLabel(group.lastSeen) })}
          </span>
          {advanced && group.deployHash ? (
            <span className="egrp__time mono" title={group.deployHash}>
              {shortSha(group.deployHash)}
            </span>
          ) : null}
        </span>
      </div>
    </li>
  );
}

export function ErrorGroupList({ label, children }: { label: string; children: ReactNode }) {
  return (
    <ul className="egrp-list" aria-label={label}>
      {children}
    </ul>
  );
}
